import React, {useRef, useState} from 'react';
import { useCheatStore } from '../../stores/cheatStore';
import { CHEAT_BG_ACTIVE, CHEAT_BORDER, CHEAT_BORDER_ACTIVE, CHEAT_TEXT } from '../../constants/cheatStyles';
import { useIsTouch } from '../../hooks/useIsTouch';

// Sits directly below the account widget (top: 12) and bug report button (top: 52) in the
// top-right stack, so it is reachable from both the map and combat.
const TOGGLE_TOP_PX = 92;
const TOGGLE_RIGHT_PX = 12;
const LABEL_MAX_WIDTH_PX = 120;
const COLLAPSE_DELAY_MS = 300;
// Collapsed and off, the bolt is the only thing on screen, so it is dimmed rather than
// hidden — present enough to find, faint enough to read as inactive at a glance.
const ICON_OFF_OPACITY = 0.55;

/**
 * Opt-in switch that reveals every cheat in the game. Off by default so players get the
 * intended experience until they deliberately ask for the shortcuts, and can switch back
 * whenever they want. Styled in the shared cheat amber so it reads as part of the same
 * family as the cheat buttons it uncovers.
 *
 * Collapses to the bolt icon alone like its neighbours in the top-right stack, expanding on
 * hover (or on tapping the icon on touch). Collapsed, the on/off state still reads at a
 * glance: on is a filled amber pill with a solid border and a bright bolt, off is a dark
 * pill with a dashed border and a dimmed bolt.
 */
export const CheatToggle: React.FC = () => {
  const cheatsEnabled = useCheatStore((s) => s.cheatsEnabled);
  const toggleCheats = useCheatStore((s) => s.toggleCheats);
  const isTouch = useIsTouch();
  const [hovered, setHovered] = useState(false);
  const [touchExpanded, setTouchExpanded] = useState(false);
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expanded = isTouch ? touchExpanded : hovered;

  const handleMouseEnter = () => {
    if (isTouch) return;
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current);
      collapseTimer.current = null;
    }
    setHovered(true);
  };

  const handleMouseLeave = () => {
    if (isTouch) return;
    collapseTimer.current = setTimeout(() => setHovered(false), COLLAPSE_DELAY_MS);
  };

  const handleIconClick = (e: React.MouseEvent) => {
    if (isTouch) {
      e.stopPropagation();
      setTouchExpanded(prev => !prev);
    }
  };

  // Mouse: clicking anywhere on the pill toggles. Touch: only the label area toggles, so the
  // icon tap that expands the pill cannot flip cheats by accident.
  const handleContainerClick = () => {
    if (!isTouch) toggleCheats();
  };

  const handleLabelClick = (e: React.MouseEvent) => {
    if (isTouch && touchExpanded) {
      e.stopPropagation();
      setTouchExpanded(false);
      toggleCheats();
    }
  };

  return (
    <div
      onClick={handleContainerClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      title={cheatsEnabled
        ? 'Cheats are on — click to hide all cheats and play as intended'
        : 'Cheats are off — click to reveal cheat controls'}
      style={{
        position: 'fixed',
        top: TOGGLE_TOP_PX,
        right: TOGGLE_RIGHT_PX,
        zIndex: 6000,
        background: cheatsEnabled ? CHEAT_BG_ACTIVE : 'rgba(10, 10, 20, 0.75)',
        border: `1px ${cheatsEnabled ? 'solid' : 'dashed'} ${cheatsEnabled ? CHEAT_BORDER_ACTIVE : CHEAT_BORDER}`,
        borderRadius: 20,
        padding: '5px 8px',
        fontFamily: 'monospace',
        fontSize: 12,
        color: cheatsEnabled ? CHEAT_TEXT : CHEAT_BORDER,
        backdropFilter: 'blur(4px)',
        cursor: 'pointer',
        pointerEvents: 'auto',
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        userSelect: 'none',
        transition: 'background 0.2s ease-in-out, color 0.2s ease-in-out',
      }}
    >
      <span
        onClick={handleIconClick}
        style={{
          opacity: cheatsEnabled ? 1 : ICON_OFF_OPACITY,
          transition: 'opacity 0.2s ease-in-out',
        }}
      >
        ⚡
      </span>
      <span
        onClick={handleLabelClick}
        style={{
          maxWidth: expanded ? LABEL_MAX_WIDTH_PX : 0,
          overflow: 'hidden',
          opacity: expanded ? 1 : 0,
          marginLeft: expanded ? 6 : 0,
          transition: 'max-width 0.2s ease-in-out, opacity 0.2s ease-in-out, margin-left 0.2s ease-in-out',
        }}
      >
        Cheats: {cheatsEnabled ? 'On' : 'Off'}
      </span>
    </div>
  );
};
